import React, { useMemo } from "react";
import { Platform, View, ViewProps } from "react-native";
import { CopilotStep, walkthroughable } from "react-native-copilot";
import { useTheme } from "styled-components/native";
import { CoachOverlayScreen } from "@services/CoachOverlayService";

const WalkthroughableView = walkthroughable(View);

export interface CoachOverlayStepProps {
  screen: CoachOverlayScreen;
  stepKey: string;
  text: string;
  order: number;
  children: React.ReactNode;
  style?: ViewProps["style"];
  webId?: string;
  active?: boolean;
}

export const CoachOverlayStep: React.FC<CoachOverlayStepProps> = ({
  screen,
  stepKey,
  text,
  order,
  children,
  style,
  webId,
  active = true,
}) => {
  const theme = useTheme();
  const isWeb = Platform.OS === "web";
  const isLargeScreen = theme.isLargeScreen;

  const domId = useMemo(() => {
    return webId || `${stepKey}-${screen}`;
  }, [webId, stepKey, screen]);

  if (!active || !text) {
    return <>{children}</>;
  }

  if (isWeb || isLargeScreen) {
    return (
      <View id={domId} style={style}>
        {children}
      </View>
    );
  }

  return (
    <CopilotStep text={text} order={order} name={stepKey}>
      <WalkthroughableView style={style}>{children}</WalkthroughableView>
    </CopilotStep>
  );
};

export interface CoachOverlayStepConfig {
  key: string;
  text: string;
  order?: number;
}

export function useCoachOverlaySteps(
  screen: CoachOverlayScreen,
  screenConfig: { steps?: CoachOverlayStepConfig[] } | null | undefined
) {
  return useMemo(() => {
    if (!screenConfig?.steps) {
      return {
        getStepProps: () => null,
        getStepByKey: () => null,
        steps: [],
      };
    }

    const steps = screenConfig?.steps || [];

    const getStepByKey = (key: string): CoachOverlayStepConfig | null => {
      return steps?.find((step) => step.key === key) || null;
    };

    const getStepProps = (
      key: string,
      overrides?: Partial<CoachOverlayStepProps>
    ): Omit<CoachOverlayStepProps, "children"> | null => {
      const step = getStepByKey(key);
      if (!step) return null;

      const index = steps?.indexOf(step) || 0;

      return {
        screen,
        stepKey: step.key,
        text: step.text,
        order: step.order ?? index + 1,
        ...overrides,
      };
    };

    return {
      getStepProps,
      getStepByKey,
      steps: steps || [],
    };
  }, [screen, screenConfig]);
}

export default CoachOverlayStep;
